<script setup lang="ts">
import { ISearchItem, ISearchResponse } from "@/repositories/search.repository"
import { ISearchCmsData } from "@/view-models/search.model"
import { computed, reactive, onMounted } from "vue";
import { useUrlSearchParams } from "@vueuse/core"
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import SearchBar from "./components/SearchBar.vue"
import SearchItem from "./components/SearchItem.vue"
import LoadMoreButton from "./components/LoadMoreButton.vue"
import { SearchService } from "@/services/search.service"

const state = reactive({
    results: [] as ISearchItem[],
    total: 0,
    query: "",
    currentPage: 1,
    perPage: 8,
    searched: false,
    loading: false,
})

const params = useUrlSearchParams()
const cmsData = CmsDataProvider.get<ISearchCmsData>(CMS_DATA_ID.SEARCH)

const noResultsTitleReplaced = computed(() => {
    return cmsData.noResultsTitle.replaceAll("{{query}}", state.query)
})

const resultCountTextReplaced = computed(() => {
    return cmsData.resultCountText.replaceAll("{{count}}", state.total.toString()).replaceAll("{{query}}", state.query)
})

const clearQuery = () => {
    state.results = []
    state.total = 0
    state.query = ""
    state.currentPage = 1
    state.searched = false

    params.page = null
    params.query = null
}

const generateUrl = () => {
    if (state.query) {
        params.query = state.query
    }
}

const getSearchResults = async (value: string): Promise<ISearchResponse> => {

    const response = await SearchService.Get({
        query: value,
        skip: state.perPage * (state.currentPage - 1),
        take: state.perPage,
        pageId: cmsData.context,
        culture: cmsData.culture,
    })

    return response.data
}

const search = async (value: string) => {
    state.query = value
    generateUrl()

    state.currentPage = 1
    state.searched = false

    const response = await getSearchResults(value)
    state.results = response.Items
    state.total = response.Total
    state.searched = true;

}

const searchMore = async (value: string) => {
    state.currentPage++;
    state.query = value

    generateUrl()

    const response = await getSearchResults(value)
    state.results.push(...response.Items)
    state.total = response.Total
    state.searched = true;
}

onMounted(() => {

    if (params.query) {
        state.query = String(params.query)

        search(state.query)
    }
})
</script>

<template>
    <form v-on:submit.prevent>
        <header class="sport-in-header pt-4 pb-10">
            <div class="container">
                <div class="row">
                    <div class="col-12 col-md-8 offset-md-2">
                        <h1 class="h2 header-title mb-4 text-center ml-0">{{ cmsData.pageTitle }}</h1>
                        <div v-if="cmsData.content" class="mb-4 header-intro" v-html="cmsData.content"></div>
                        <search-bar :button-text="cmsData.searchButtonText" :query="state.query"
                            :placeholder="cmsData.searchbarPlaceholder" @execute-search="search($event)"
                            @clear-query="clearQuery" />
                    </div>
                </div>
            </div>
        </header>
    </form>

    <div v-if="state.searched" class="container-lg mt-n4 bg-white p-4 artikelen-tab">
        <div class="row">
            <div>
                <div v-if="state.query.length > 0 && state.total === 0">
                    <h5 v-text="noResultsTitleReplaced" />
                    <p v-text="cmsData.noResultsText" />
                </div>
            </div>
            <div class="col-12" v-if="state.results.length">
                <div>
                    <p v-if="state.query.length > 0" class="text-center" v-text="resultCountTextReplaced" />
                </div>
                <search-item v-for="item in state.results" :key="item.Id" :cms-data="cmsData" :item="item" />
                <div class="d-flex justify-content-center">
                    <load-more-button :button-text="cmsData.showMoreText" :aria-label="cmsData.showMoreText"
                        v-if="state.total > (state.currentPage * state.perPage)" @load-more="searchMore(state.query)" />
                </div>
            </div>
        </div>
    </div>
</template>